import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { trackPageView } from "@/lib/analytics";

/**
 * Loads GA4 + Meta Pixel + Search Console verification from /api/settings/public
 * and injects the right snippets into <head>. Also fires `page_view` on every
 * route change.
 *
 * All IDs are admin-configurable from /admin → SEO & Indexing tab.
 *
 * GA4 STUB BOOTSTRAP — defined at module top so any event fired BEFORE the
 * async settings fetch completes is buffered into window.dataLayer and replayed
 * once gtag.js loads. Fixes the begin_checkout/purchase race condition.
 */
if (typeof window !== "undefined") {
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
        // eslint-disable-next-line no-inner-declarations
        window.gtag = function () { window.dataLayer.push(arguments); };
    }
    // Meta Pixel pre-queue: capture fbq() calls fired before fbevents.js loads.
    // ensureFbPixel() replays these after the FB snippet installs the real fbq.
    window.__fb_pre_queue = window.__fb_pre_queue || [];
    if (!window.fbq) {
        window.fbq = function () { window.__fb_pre_queue.push(arguments); };
    }
}

let _settingsPromise = null;
function getSettings() {
    if (!_settingsPromise) {
        _settingsPromise = api.get("/settings/public").then((r) => r.data).catch(() => ({}));
    }
    return _settingsPromise;
}

let _gtagInjected = false;

export default function Analytics() {
    const location = useLocation();
    const [settings, setSettings] = useState(null);

    useEffect(() => {
        getSettings().then((s) => {
            setSettings(s || {});
            if (s?.ga4_measurement_id) {
                window.__GA4_ID = s.ga4_measurement_id;
                ensureGtag(s.ga4_measurement_id);
            }
            if (s?.meta_pixel_id) {
                ensureFbPixel(s.meta_pixel_id);
            }
        });
    }, []);

    // Fire SPA page_view on every route change
    useEffect(() => {
        if (window.gtag && window.__GA4_ID) {
            window.gtag("config", window.__GA4_ID, { page_path: location.pathname + location.search });
        }
        trackPageView(location.pathname + location.search);
    }, [location.pathname, location.search]);

    return (
        <Helmet>
            {settings?.gsc_verification && (
                <meta name="google-site-verification" content={settings.gsc_verification} />
            )}
            {settings?.bing_verification && (
                <meta name="msvalidate.01" content={settings.bing_verification} />
            )}
        </Helmet>
    );
}

function ensureGtag(id) {
    if (_gtagInjected) return;
    _gtagInjected = true;
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    document.head.appendChild(script);
    // window.gtag + dataLayer are already bootstrapped at module top
    window.gtag("js", new Date());
    window.gtag("config", id, { send_page_view: false }); // we'll send on every route change
}

function ensureFbPixel(id) {
    // Mark whether the real Pixel snippet has already installed. We detect it
    // via the `loaded` flag the official snippet sets.
    if (window.fbq && window.fbq.loaded) return;
    // Capture any events queued via our pre-stub BEFORE the FB snippet replaces window.fbq
    const preQueue = (window.__fb_pre_queue || []).slice();
    window.__fb_pre_queue = [];
    /* eslint-disable */
    !function(f,b,e,v,n,t,s){if(f.fbq && f.fbq.loaded)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
        n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
        document,'script','https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */
    window.fbq("init", id);
    window.fbq("track", "PageView");
    // Replay any events that fired before the snippet installed
    preQueue.forEach((args) => { try { window.fbq.apply(null, args); } catch (e) { /* ignore */ } });
}
